import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ClientFormRequestStatus } from "../../../../common/enums";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  applyThemeColors,
  generateHexFromString,
  Default_Background_Primary_Color,
  Default_Background_Secondary_Color,
  Default_Button_Background_Color,
  Default_Button_Text_Color,
} from "../../../../common/infrastructure/utilities/colorUtility";
import { usePreReservationFormAnswers } from "../../../../common/preReservationForms/usePreReservationFormAnswers";
import {
  FillClientFormView,
  ISentClientFormModel,
} from "../models/fillClientFormModels";

const viewByStatus: Record<ClientFormRequestStatus, FillClientFormView> = {
  [ClientFormRequestStatus.Pending]: "form",
  [ClientFormRequestStatus.Completed]: "completed",
  [ClientFormRequestStatus.Expired]: "expired",
  [ClientFormRequestStatus.Unavailable]: "unavailable",
};

// The API sends UTC without a zone designator; read it as UTC, not as local time.
const formatUtcDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${date.getFullYear()}`;
};

/**
 * The page a client opens from a form staff emailed them: loads the form by the link's token
 * (no login), fills it in once and says what happened. The studio's colors come from the
 * response, since an anonymous visitor has no selected studio.
 */
export const useFillClientFormPage = () => {
  const { token = "" } = useParams<{ token: string }>();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data, isLoading, isError } = useApiQuery<ISentClientFormModel>({
    queryKey: [endpointsMetadata.clientForm.getSentForm, token],
    endpoint: `${endpointsMetadata.clientForm.getSentForm}/${encodeURIComponent(token)}`,
    enabled: !!token,
    globalIsLoading: false,
    retry: false,
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    applyThemeColors(
      generateHexFromString(data.backgroundColorPrimary || Default_Background_Primary_Color),
      generateHexFromString(data.backgroundColorSecondary || Default_Background_Secondary_Color),
      generateHexFromString(data.buttonBackgroundColor || Default_Button_Background_Color),
      generateHexFromString(data.buttonTextColor || Default_Button_Text_Color),
    );
  }, [data]);

  const { segments, answers, setAnswer, isInvalid, showMissingError, collectAnswers } =
    usePreReservationFormAnswers(data?.templateText ?? "");

  const { mutate, isLoading: isSubmitting } = useApiMutation<string>();

  const submit = () => {
    if (isSubmitted || isSubmitting) {
      return;
    }

    const collected = collectAnswers();
    if (!collected) {
      return;
    }

    mutate(
      {
        endpoint: `${endpointsMetadata.clientForm.submitSentForm}/${encodeURIComponent(token)}`,
        method: "post",
        payload: { answers: collected },
        disableSuccessToast: true,
        disable401Redirect: true,
      },
      { onSuccess: () => setIsSubmitted(true) },
    );
  };

  let view: FillClientFormView;
  if (!token || isError || (!isLoading && !data)) {
    view = "invalid";
  } else if (isLoading || !data) {
    view = "loading";
  } else if (isSubmitted) {
    view = "submitted";
  } else {
    view = viewByStatus[data.status] ?? "invalid";
  }

  return {
    view,
    studioName: data?.studioName ?? "",
    formName: data?.formName ?? "",
    completedOnText: formatUtcDate(data?.completedOn),
    segments,
    contextValues: data?.contextValues ?? {},
    answers,
    setAnswer,
    isInvalid,
    showMissingError,
    isSubmitting,
    submit,
  };
};
